// ProjectedQuestionV1 — the frozen 5→6 seam contract (#4681 E2).
//
// E1 (SEAM-INVENTORY.md, merged at b56da9f8) proved the stage-5→6 handoff had
// no versioned artifact: the raw question string threaded through every layer,
// the de-facto compiled question was an unversioned validated-capability-call
// object minted by hand at FOUR sites in ask-helix.query-runtime.service.ts,
// and the bridge gated it with a hand-rolled guard. This file is the single
// source (Law 5): one canonical type, one mint constructor, one runtime
// validator. The apps/api bridge types become aliases of these — the api
// never re-declares the shape.
//
// Deliberate scope: the raw question string is WRAPPED, not replaced (E1
// proved nothing downstream can lose it). Stage-6 authorities
// (ExecutiveAnswerContract, compiler admissions, binding identity) stay with
// #4619 — this contract ends at module execution input.

import type { DecisionAgentContextPack } from '../../decision-agent-platform';
import type { WorkspaceEvidenceReceipt } from './workspace-context';
import type { ManagerValidatedArgumentAuthority } from './planner-decision';

export const PROJECTED_QUESTION_SCHEMA_VERSION = 'projected_question_v1' as const;

export const PROJECTED_QUESTION_SELECTED_BY = [
  'ask_helix_llm_tool_planner',
  'manifest_exact_question_binding',
  'explicit_module_binding',
] as const;

export type ProjectedQuestionSelectedBy =
  (typeof PROJECTED_QUESTION_SELECTED_BY)[number];

export type ProjectedQuestionModuleRoutingV1 = {
  selectedBy: ProjectedQuestionSelectedBy;
  reason?: string | null;
  selectedFamilyId?: string | null;
  decisionFamilyId?: string | null;
  validatedByCapabilityValidator?: boolean;
};

export type ProjectedQuestionValidatedCallV1 = {
  capabilityId: string;
  toolName: string;
  intentId: string;
  intentKind: string;
  /**
   * #5520 child 1 — the ordered, per-requirement authority carrier. No
   * free-form property bag, no raw-text alias, no index signature.
   */
  arguments: readonly ManagerValidatedArgumentAuthority[];
  /** Capability-validator-only digest binding capability, intent, arguments and tenant. */
  validationReceipt: string;
  /** Digit-free disclosure sentences recorded by the capability validator when
   * an absent required input was resolved to its manifest-documented default.
   * Absent when every required input was caller-supplied. */
  defaultedInputDisclosures?: readonly string[];
  evidenceReceipts?: readonly WorkspaceEvidenceReceipt[];
};

export type ProjectedQuestionAuthorityScopeV1 = {
  type: string;
  rootNodeId: string | null;
};

export type ProjectedQuestionV1 = {
  schemaVersion: typeof PROJECTED_QUESTION_SCHEMA_VERSION;
  question: string;
  moduleIdHint?: string | null;
  moduleRouting?: ProjectedQuestionModuleRoutingV1 | null;
  selectedFamilyId?: string | null;
  decisionFamilyId?: string | null;
  tenantId?: string | null;
  actorUserId?: string | null;
  actorPermissions?: readonly string[] | null;
  authorityScope?: ProjectedQuestionAuthorityScopeV1 | null;
  idempotencyKey?: string | null;
  runtimeMode?: 'product' | 'preview-local' | null;
  contextPack?: DecisionAgentContextPack | null;
  operationPlan?: unknown | null;
  scenarioOptIn?: boolean | null;
  trustedConstraintAck?: {
    disableExecutiveProtection?: boolean | null;
  } | null;
  validatedCapabilityCall?: ProjectedQuestionValidatedCallV1 | null;
};

// The validator output the four mint sites project from. Structurally the
// capability validator's ValidatedCapabilityCall minus its manifest/intent
// carries — declared here so the mint path never reaches back into apps/api.
export type ProjectedQuestionMintCall = {
  capabilityId: string;
  toolName: string;
  intentId: string;
  intentKind: string;
  arguments: readonly ManagerValidatedArgumentAuthority[];
  validationReceipt: string;
  defaultedInputDisclosures?: readonly string[];
  evidenceReceipts?: readonly WorkspaceEvidenceReceipt[];
};

export type MintProjectedQuestionInput = Omit<
  ProjectedQuestionV1,
  'schemaVersion' | 'validatedCapabilityCall'
> & {
  validatedCall: ProjectedQuestionMintCall;
};

export const PROJECTED_QUESTION_VALIDATION_RECEIPT_PREFIX = 'capability-validation-v1';

export type ProjectedQuestionValidationReceiptInput = Readonly<{
  tenantId: string;
  capabilityId: string;
  intentId: string;
  arguments: readonly ManagerValidatedArgumentAuthority[];
}>;

// `isProjectedQuestionValidationReceipt` is a pure string-shape check (prefix +
// hex digest format) — no crypto, safe in the client-reachable barrel. The
// functions that actually COMPUTE or VERIFY the digest
// (`createProjectedQuestionValidationReceipt`, `projectedQuestionValidationReceiptMatches`)
// and the full validator/mint path that calls them
// (`validateProjectedQuestion`, `upgradeToProjectedQuestionV1`,
// `mintProjectedQuestion`) moved to the Node-only sibling
// `./projected-question-server` (#4051 P1 fix): `node:crypto` was reachable
// from the PUBLIC, client-bundled `@helix/lib` barrel through this file,
// breaking the apps/web build. Import them from `@helix/lib/server` instead
// of from here.
export function isProjectedQuestionValidationReceipt(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const [prefix, digest, extra] = value.split(':');
  return extra === undefined && prefix === PROJECTED_QUESTION_VALIDATION_RECEIPT_PREFIX &&
    /^[a-f0-9]{64}$/.test(digest ?? '');
}
